import {
  Autocomplete,
  Box,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Partner } from '@provision/entity-types';
import { useEffect, useMemo, useState } from 'react';
import {
  Address,
  PartnerGroupRules,
  useBrandOptions,
  useMembershipsActivationFlowConfig,
  usePartnerGroupRules,
} from 'src/graphql';
import { NullableDeepPartial } from 'src/graphql/types';
import { MembershipFeatures } from './membership-features';

interface MembershipTabProps {
  partner: NullableDeepPartial<Pick<Partner, 'partnerId'>>;
  address?: Partial<Address>;
}

const defaultInput = {
  id: '',
  label: '',
};

const MembershipSkeleton = () => (
  <Stack spacing={2}>
    {[1, 2, 3].map((el) => (
      <Box key={`Skeleton${el}`}>
        <Skeleton variant="text" height="3rem" width="40%" />
        <Stack p="0 1rem" spacing={1}>
          <Skeleton variant="rounded" height="1rem" width="80%" />
          <Skeleton variant="rounded" height="1rem" width="80%" />
          <Skeleton variant="rounded" height="1rem" width="80%" />
        </Stack>
      </Box>
    ))}
  </Stack>
);

export const MembershipTab = ({ partner, address }: MembershipTabProps) => {
  const theme = useTheme();

  const [input, setInput] = useState<{ id: string; label: string }>(defaultInput);
  const optionBrands = useBrandOptions(partner?.partnerId);

  const associatedBrand = useMemo(
    () => optionBrands.find((brand) => brand.id === address?.brandId),
    [address?.brandId, optionBrands],
  );

  useEffect(() => {
    setInput(associatedBrand ?? optionBrands[0] ?? defaultInput);
  }, [associatedBrand, optionBrands]);

  const { config, loading: loadingConfig } = useMembershipsActivationFlowConfig({
    partnerId: partner?.partnerId ?? null,
    brandId: input?.id || null,
  });

  const showMonthlyPrices = config?.options?.showMonthlyPrices ?? true;
  const showAnnualPrices = config?.options?.showAnnualPrices ?? false;

  const membershipFeatures = useMemo(() => {
    if (!config?.products?.length) return [];

    return config.products
      .map((product) => {
        const features = product.activeFeatures
          .map((af) => {
            const featureDef = config.features.find(
              (f) => f.membershipFeatureId === af.membershipFeatureId,
            );
            if (!featureDef) return null;
            return {
              featureId: af.membershipFeatureId,
              content: af.overrideContent || featureDef.defaultContent || '',
              tooltip: af.overrideTooltip || featureDef.defaultTooltip || undefined,
              sortOrder: featureDef.sortOrder,
            };
          })
          .filter((f): f is NonNullable<typeof f> => f !== null)
          .sort((a, b) => a.sortOrder - b.sortOrder);

        return {
          displayName: product.name,
          monthlyPriceCents: product.prices.monthly?.amountCents ?? null,
          annualPriceCents: product.prices.annual?.amountCents ?? null,
          callForPricing: product.callForPricing ?? false,
          features,
        };
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [config]);

  const { partnerGroupRules } = usePartnerGroupRules(address?.partnerId ?? partner?.partnerId);

  const hasAccessToMembershipInfo =
    partnerGroupRules[PartnerGroupRules.AccessToSubscriptionManagement];

  if (!hasAccessToMembershipInfo) {
    return (
      <Typography
        variant="body1"
        color="textSecondary"
        textAlign="center"
        mt={1}
        sx={{ whiteSpace: 'normal', maxWidth: '100%' }}
      >
        Partner does not have access to subscription management. Do not engage in membership
        conversations.
      </Typography>
    );
  }

  return (
    <Stack
      spacing={2}
      sx={{
        paddingTop: 1,
      }}
    >
      <Autocomplete
        autoHighlight
        fullWidth
        value={input}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder="Assign"
            inputProps={{
              ...params.inputProps,
              sx: {
                ...theme.typography.body2,
                fontSize: theme.utils.important('0.875rem'),
              },
            }}
          />
        )}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        options={optionBrands}
        onChange={(_, value) => {
          setInput(value ?? defaultInput);
        }}
      />
      <Stack>
        {loadingConfig ? (
          <MembershipSkeleton />
        ) : (
          <MembershipFeatures
            featureMemberships={membershipFeatures}
            showMonthlyPrices={showMonthlyPrices}
            showAnnualPrices={showAnnualPrices}
          />
        )}
      </Stack>
    </Stack>
  );
};
